// import { delay } from 'redux-saga';
import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import {
  fetchFields,
  fetchFieldDefinitions,
  reorderCustomAttributes,
  createAttribute,
  deleteField,
  editAttribute,
  fetchSystemFields,
  editSystemFields,
  fetchFieldUsage,
} from '../routines';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import constants from '../../common/constants/CommonConstants';
import {
  ACCOUNT,
  CONTACT,
  PIPELINE,
  ORGANIZATION,
} from '../../common/constants/TableColumnScopeConstants';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { stripHtml } from '../../common/util/EmailUtils';

const { ACCOUNT_LEVEL, ORGANIZATION_LEVEL, CONTACT_LEVEL, PIPELINE_LEVEL } =
  constants;
const levelScopeMap = {
  [ACCOUNT_LEVEL]: ACCOUNT,
  [ORGANIZATION_LEVEL]: ORGANIZATION,
  [CONTACT_LEVEL]: CONTACT,
  [PIPELINE_LEVEL]: PIPELINE,
};
export async function validateFieldValuesExist({
  level,
  displayName,
  apiName,
}) {
  const format = 'list';
  let URL = `${envConfig.APIG_URL}core/fields?format=${format}&level=${level}`;
  if (displayName) {
    URL = `${URL}&displayName=${encodeURIComponent(displayName)}`;
  } else if (apiName) {
    URL = `${URL}&name=${encodeURIComponent(apiName)}`;
  } else {
    throw new Error('No Value Entered!');
  }

  try {
    const response = await request(URL);
    return response;
  } catch (error) {
    return null;
  }
}

export function* fetchFieldsSaga({ payload }) {
  const format = payload.format || 'object';
  const { level, withUsage } = payload;
  const usageValue = withUsage === true;
  if (!level) throw new TypeError('Level cannot be null or undefined');
  try {
    const URL = `${envConfig.APIG_URL}core/fields?format=${format}&level=${level}&withUsage=${usageValue}`;
    const response = yield call(request, URL);
    yield put(fetchFields.success({ response, level }));
  } catch (error) {
    yield put(fetchFields.failure({ error, level }));
  }
}

export function* deleteFieldSaga({ payload }) {
  const { level, customColumnId, name } = payload;
  try {
    const URL = `${envConfig.APIG_URL}core/fields/${payload.customColumnId}`;
    yield call(request, URL, {
      method: 'DELETE',
    });

    yield put(
      deleteField.success({
        name,
        customColumnId,
        level,
        successMessage: SUCCESS_MESSAGES.DELETE_ATTRIBUTE,
      })
    );
    yield put(fetchFields.trigger({ level, withUsage: true }));
    yield put(
      fetchFieldDefinitions.trigger({ level, scope: levelScopeMap[level] })
    );
  } catch (error) {
    yield put(deleteField.failure({ error, level }));
  }
}

export function* editAttributeSaga({ payload }) {
  const { level } = payload;
  try {
    const body = {
      name: payload.name,
      displayName: payload.displayName,
      dataType: payload.dataType,
      columnLevel: payload.columnLevel,
      description: payload.description,
      vertical: payload.vertical || false,
      horizontal: payload.horizontal || false,
      horizontalCustom: payload.horizontalCustom || false,
      required: payload.required || false,
      isVisible: payload.isVisible || false,
      isEditable: payload.isEditable || false,
      isDefault: payload.isDefault || false,
      isUnique: payload.isUnique || false,
    };
    if (payload.fieldOptions) {
      // orderType property must be present
      body.fieldOptions = payload.fieldOptions;
    }

    if (payload.vertical) {
      body.childId = payload.childId;
    }
    if (payload.horizontal) {
      body.calculatedFields = payload.calculatedFields;
    }
    if (payload.horizontalCustom) {
      body.rawAdvCalculation =
        payload.rawAdvCalculation || payload.rawCalculation;
      body.calcDefinition = stripHtml(
        JSON.parse(payload.rawAdvCalculation || payload.rawCalculation)
      );
    }
    if (
      payload.dataType === 'singlePicklist' ||
      payload.dataType === 'multiPicklist'
    ) {
      body.picklistDefinition = payload.picklistDefinition;
    }

    const URL = `${envConfig.APIG_URL}core/fields/${payload.customColumnId}`;

    const response = yield call(request, URL, {
      method: 'PUT',
      payload: body,
    });

    yield put(
      editAttribute.success({
        response,
        level,
        successMessage: SUCCESS_MESSAGES.EDIT_ATTRIBUTE,
      })
    );
    yield put(fetchFields.trigger({ level, withUsage: true }));
    yield put(
      fetchFieldDefinitions.trigger({ level, scope: levelScopeMap[level] })
    );
  } catch (error) {
    yield put(editAttribute.failure({ error, level }));
  }
}

export function* createAttributeSaga({ payload }) {
  const { level } = payload;
  try {
    const body = {
      name: payload.name,
      displayName: payload.displayName,
      dataType: payload.dataType,
      columnLevel: payload.columnLevel,
      description: payload.description,
      vertical: payload.vertical || false,
      horizontal: payload.horizontal || false,
      horizontalCustom: payload.horizontalCustom || false,
      required: payload.required || false,
      isVisible: payload.isVisible || false,
      isEditable: payload.isEditable || false,
      isDefault: payload.isDefault || false,
      fieldOptions: payload.fieldOptions,
      isUnique: payload.isUnique || false,
    };

    if (payload.vertical) {
      body.childId = payload.childId;
    }
    if (payload.horizontal) {
      body.calculatedFields = payload.calculatedFields;
    }
    if (payload.horizontalCustom) {
      body.rawAdvCalculation = payload.rawAdvCalculation;
      body.calcDefinition = stripHtml(JSON.parse(payload.rawAdvCalculation));
    }
    if (
      payload.dataType === 'singlePicklist' ||
      payload.dataType === 'multiPicklist'
    ) {
      body.picklistDefinition = payload.picklistDefinition;
    }

    const URL = `${envConfig.APIG_URL}core/fields`;

    const response = yield call(request, URL, {
      method: 'POST',
      payload: body,
    });

    yield put(
      createAttribute.success({
        response,
        level,
        successMessage: SUCCESS_MESSAGES.ADD_ATTRIBUTE,
      })
    );
    yield put(fetchFields.trigger({ level, withUsage: true }));
    yield put(
      fetchFieldDefinitions.trigger({ level, scope: levelScopeMap[level] })
    );
  } catch (error) {
    yield put(createAttribute.failure({ error, level }));
  }
}
export function* reorderCustomAttributesSaga({ payload }) {
  const { level } = payload;
  try {
    const body = {
      columnLevel: payload.columnLevel,
      orderArray: payload.orderArray,
    };
    const URL = `${envConfig.APIG_URL}core/field-order`;

    yield call(request, URL, {
      method: 'PUT',
      payload: body,
    });
    yield put(reorderCustomAttributes.success(payload));
  } catch (error) {
    yield put(reorderCustomAttributes.failure({ error, level }));
  }
}

export function* fetchSystemFieldsSaga() {
  try {
    const URL = `${envConfig.APIG_URL}core/fields/system`;
    const response = yield call(request, URL);
    yield put(fetchSystemFields.success({ response }));
  } catch (error) {
    yield put(fetchSystemFields.failure({ error }));
  }
}

export function* editSystemFieldsSaga({ payload }) {
  try {
    const { systemFieldId, ...restPayload } = payload;

    let body = {};
    if (payload?.fieldOptions) {
      body = restPayload;
    } else {
      body = {
        required: restPayload?.required,
        isEditable: restPayload?.isEditable,
      };
    }

    const URL = `${envConfig.APIG_URL}core/fields/system/${systemFieldId}`;
    const response = yield call(request, URL, {
      method: 'PUT',
      payload: body,
    });
    yield put(
      editSystemFields.success({
        response,
        systemField: payload,
        successMessage: SUCCESS_MESSAGES.EDIT_ATTRIBUTE,
      })
    );
    yield put(fetchSystemFields.trigger());
  } catch (error) {
    yield put(editSystemFields.failure({ error }));
  }
}

export function* fetchFieldUsageSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}core/fields/${payload.fieldId}/system-usage`;
    const response = yield call(request, URL);

    yield put(
      fetchFieldUsage.success({
        response,
      })
    );
  } catch (error) {
    yield put(fetchFieldUsage.failure({ error }));
  }
}

export default function* root() {
  yield all([
    takeEvery(fetchFields.TRIGGER, fetchFieldsSaga),
    takeLatest(reorderCustomAttributes.TRIGGER, reorderCustomAttributesSaga),
    takeLatest(createAttribute.TRIGGER, createAttributeSaga),
    takeLatest(editAttribute.TRIGGER, editAttributeSaga),
    takeLatest(deleteField.TRIGGER, deleteFieldSaga),
    takeLatest(fetchSystemFields.TRIGGER, fetchSystemFieldsSaga),
    takeLatest(editSystemFields.TRIGGER, editSystemFieldsSaga),
    takeLatest(fetchFieldUsage.TRIGGER, fetchFieldUsageSaga),
  ]);
}
